/**
 * Every generated CRF set is held against the facts the protocol reading
 * captured (Jim, 2026-10-02): REPAIR what is deterministic, otherwise BLOCK
 * approval with a plain reason. The target is no wrong forms, and every fact the
 * protocol states either honoured or flagged.
 *
 * This module is PURE and browser-safe (it reaches no catalog, no Firestore and
 * no model), so the CRF Builder's approval gate runs the very same code the
 * generator and the verification family run.
 *
 * Fourteen checks, W1 to W14. The id is internal; the screen says the title.
 *
 * BLOCKING RULE (lead, 2026-10-02): a check may BLOCK approval only when it is precise,
 * that is, it compares a form with an explicit list the protocol states. Anything that rests
 * on reading free text is a WARN: shown, never blocking. A repair may only ADD a missing
 * field or option, or REPLACE an option list with the protocol's explicit list; the only
 * removals are the dose options a full sentence forbids and a yes/no flag asked twice.
 *
 *   W1  adverse event severity equals the protocol's scale          repairs, BLOCKS
 *   W2  adverse event causality equals the protocol's scale         repairs, BLOCKS
 *   W3  every demographic the protocol collects has a field         repairs a clear one, BLOCKS only for it
 *   W4  race / ethnicity the protocol does not collect (non-US)     WARN
 *   W5  every stratification factor is captured on Randomisation    repairs, BLOCKS
 *   W6  no dose reduced / increased option when none is allowed     repairs, WARN
 *   W7  oncology leftovers in a non-oncology study                  WARN, never repaired
 *   W8  a form built on an instrument the protocol never names      WARN
 *   W9  every seriousness criterion has a field on the SAE form     repairs, WARN
 *   W10 no duplicate yes/no flag on the SAE form                    repairs, WARN
 *   W11 yes/no codes equal the protocol's coding convention         repairs, BLOCKS
 *   W12 sex codes equal the protocol's coding convention            repairs, BLOCKS
 *   W13 a scheduled follow-up contact collects safety information   WARN
 *   W14 PK sampling times are ones the protocol schedules           WARN
 *
 * ABSENT INPUT IS NEVER A PASS. A check whose protocol fact is not in the
 * reading, or whose form is not in the set, is SKIPPED with its reason.
 *
 * REPAIRS are data, not side effects: a check returns `repair`, a list of
 * operations, and `repairProtocolFacts` applies them and re-runs the checks.
 * Every operation carries a `source` ("protocol section 9.2 (automatic
 * correction)") that lands in the field's `codesFrom`.
 */
import { singleSexRestriction, restrictSexOptions } from "./studySex";
import {
  classifyVariable, protocolList, factsOf, sectionOf, parseCodePairs, demographicNotCollected,
  isStudyWideDoseDenial, DOSE_PERMIT_WORDS, DOSE_DENIAL, corpusOf,
  type AuthVariable,
} from "./crfProtocolReading";
import { collectAssessments } from "../schedule/scheduleCoverage";

// ── Types ──────────────────────────────────────────────────────────────────

export type PfCheckId = `W${number}`;
export interface PfOption { code: number | string; label: string }
export interface PfField { varName: string; text: string; responseType: string; options: PfOption[]; codesFrom?: string }
export interface PfForm { name: string; status?: string; fields: PfField[] }

export type PfOp =
  | { kind: "setOptions"; form: string; varName: string; options: PfOption[]; responseType?: string; source: string; why: string }
  | { kind: "addField"; form: string; field: PfField; source: string; why: string }
  | { kind: "removeOptions"; form: string; varName: string; labels: string[]; source: string; why: string }
  | { kind: "removeField"; form: string; varName: string; source: string; why: string };

export interface PfResult {
  id: PfCheckId;
  status: "PASS" | "FAIL" | "SKIPPED";
  /** Plain title, as the screen says it. */
  title: string;
  detail: string;
  /** Why nothing was examined (SKIPPED only). */
  reason?: string;
  /** One plain sentence per offending item. */
  items: string[];
  /** A FAIL that holds Approve back. Skipped and passing checks never block. */
  blocking: boolean;
  repair?: PfOp[];
}

export interface PfInput {
  /** protocolAnalysis/latest `.analysis`. */
  analysis: any;
  /** projects/{id}.studySetup. */
  studySetup?: any;
  /** studyDesign/config. */
  studyDesign?: any;
  /** crfPackage/visitMapping. */
  visitMapping?: any;
  /** CRF Builder forms: sections/questions, or variables. */
  forms: any[];
  /** The protocol text, when the caller has it. */
  protocolText?: string;
}

export interface PfApplied { form: string; varName: string; kind: PfOp["kind"]; why: string; source: string }

// ── Reading the forms ──────────────────────────────────────────────────────

const str = (x: unknown): string => (typeof x === "string" ? x : x == null ? "" : String(x));
const low = (x: unknown): string => str(x).toLowerCase();
const squash = (x: unknown): string => low(x).replace(/\s+/g, " ").trim();

function optionsOf(raw: unknown): PfOption[] {
  if (!Array.isArray(raw)) return [];
  const out: PfOption[] = [];
  for (const o of raw) {
    if (typeof o === "string") { if (o.trim()) out.push({ code: o.trim(), label: o.trim() }); continue; }
    const label = str((o as any)?.label ?? (o as any)?.text);
    const code = (o as any)?.code;
    if (!label.trim()) continue;
    out.push({ code: typeof code === "number" || (typeof code === "string" && code !== "") ? code : label.trim(), label: label.trim() });
  }
  return out;
}

function fieldsOf(raw: any): PfField[] {
  const out: PfField[] = [];
  const push = (varName: unknown, text: unknown, responseType: unknown, options: unknown, codesFrom: unknown) => {
    const v = str(varName).trim();
    if (!v && !str(text).trim()) return;
    out.push({ varName: v, text: str(text).trim(), responseType: str(responseType) || "text", options: optionsOf(options), ...(str(codesFrom) ? { codesFrom: str(codesFrom) } : {}) });
  };
  // Already-normalised forms (PfForm) carry `fields`.
  for (const f of Array.isArray(raw?.fields) ? raw.fields : []) push(f?.varName, f?.text, f?.responseType, f?.options, f?.codesFrom);
  if (out.length > 0) return out;
  for (const sec of Array.isArray(raw?.sections) ? raw.sections : []) {
    for (const q of Array.isArray(sec?.questions) ? sec.questions : []) push(q?.variable ?? q?.varName, q?.text ?? q?.questionText, q?.response_type ?? q?.responseType, q?.options, q?.codesFrom);
  }
  if (out.length === 0) {
    for (const v of Array.isArray(raw?.variables) ? raw.variables : []) push(v?.varName ?? v?.variable, v?.questionText ?? v?.text, v?.responseType ?? v?.response_type, v?.options, v?.codesFrom);
  }
  return out;
}

/** Forms as the CRF Builder holds them (sections/questions or variables), normalised. */
export function toPfForms(raw: any[]): PfForm[] {
  return (Array.isArray(raw) ? raw : [])
    .filter((f) => typeof f?.name === "string" && f.name.trim())
    .map((f) => ({ name: f.name.trim(), ...(typeof f.status === "string" ? { status: f.status } : {}), fields: fieldsOf(f) }));
}

const asVar = (f: PfField): AuthVariable => ({ varName: f.varName, questionText: f.text, responseType: f.responseType, options: f.options });
const concept = (f: PfField) => classifyVariable(asVar(f));
const ftext = (f: PfField) => `${f.varName} ${f.text}`.toLowerCase();

const isAeForm = (n: string) => /adverse event|\bAEs?\b|\bSAE\b/i.test(n);
const isSaeForm = (n: string) => /serious adverse|\bSAE\b/i.test(n);
const isDemoForm = (n: string) => /demograph/i.test(n);
const isRandForm = (n: string) => /randomi[sz]ation/i.test(n);
const isPkForm = (n: string) => /pharmacokinetic|\bPK\b/i.test(n);

// ── Reading the protocol ───────────────────────────────────────────────────

/** A list of strings from a fact class stored either as strings or as {text}. */
function listOf(x: unknown): string[] {
  if (!Array.isArray(x)) return [];
  return x.map((i) => (typeof i === "string" ? i : str((i as any)?.text ?? (i as any)?.name))).map((s) => s.trim()).filter(Boolean);
}

/** Everything the reading says under the named keys, as one lower-case string. */

const sourceFor = (facts: Array<{ text: string; section?: string }>) => `${sectionOf(facts)} (automatic correction)`;

function sameOptions(a: PfOption[], b: PfOption[]): boolean {
  return a.length === b.length && a.every((o, i) => String(o.code) === String(b[i].code) && squash(o.label) === squash(b[i].label));
}
const fromSponsor = (f: PfField) => /^sponsor dictionary/i.test(f.codesFrom ?? "");
/** Same options as a set: order does not matter. */
function sameOptionSet(a: PfOption[], b: PfOption[]): boolean {
  const k = (o: PfOption) => `${String(o.code)}|${squash(o.label)}`;
  const sa = new Set(a.map(k));
  return a.length === b.length && b.every((o) => sa.has(k(o)));
}
/** Whole-word match, so "age" never matches "stage". */
const hasWord = (text: string, word: string) => new RegExp(`(^|[^a-z0-9])${word.replace(/[^a-z0-9]/g, "")}([^a-z0-9]|$)`).test(text);
const optString = (o: PfOption[]) => o.map((x) => `${x.code}=${x.label}`).join("; ");

// ── Result helpers ─────────────────────────────────────────────────────────

export const PF_TITLES: Record<number, string> = {
  1: "Adverse event severity uses the protocol's scale",
  2: "Adverse event relationship uses the protocol's scale",
  3: "Every demographic item the protocol collects has a field",
  4: "No race or ethnicity field the protocol does not collect",
  5: "Every randomisation stratification factor is recorded",
  6: "No dose change option the protocol does not allow",
  7: "No cancer-study items in a study that is not about cancer",
  8: "Every named questionnaire is one the protocol names",
  9: "The serious adverse event form has a field for each seriousness criterion",
  10: "The serious adverse event form asks each question once",
  11: "Yes and No are coded the way the protocol codes them",
  12: "Sex is coded the way the protocol codes it",
  13: "Every scheduled follow-up contact has a form that collects safety information",
  14: "Pharmacokinetic sample times are the ones the protocol schedules",
};

const pass = (n: number, detail: string): PfResult => ({ id: `W${n}`, status: "PASS", title: PF_TITLES[n], detail, items: [], blocking: false });
const skip = (n: number, reason: string): PfResult => ({ id: `W${n}`, status: "SKIPPED", title: PF_TITLES[n], detail: `Not checked: ${reason}.`, reason, items: [], blocking: false });
const fail = (n: number, detail: string, items: string[], blocking: boolean, repair?: PfOp[]): PfResult => ({
  id: `W${n}`, status: "FAIL", title: PF_TITLES[n], detail, items, blocking, ...(repair && repair.length ? { repair } : {}),
});

interface Ctx {
  analysis: any;
  setup: any;
  design: any;
  mapping: any;
  forms: PfForm[];
  text: string;
}

// ── W1 / W2: scales ────────────────────────────────────────────────────────

/** CTCAE grading is a published scale: "CTCAE ... Grade 1-5" carries its named grades. */
function ctcaeScale(analysis: any): { options: PfOption[]; source: string } | null {
  for (const f of factsOf(analysis, "aeSeverityScale")) {
    const m = /ctcae/i.test(f.text) ? /grade\s*1\s*(?:-|–|to|through)\s*([345])/i.exec(f.text) : null;
    if (!m) continue;
    const names = ["Mild", "Moderate", "Severe", "Life-threatening", "Death"];
    const n = Number(m[1]);
    return { options: names.slice(0, n).map((label, i) => ({ code: i + 1, label: `Grade ${i + 1} - ${label}` })), source: sourceFor([f]) };
  }
  return null;
}

/** A severity scale for labs ("for laboratory toxicity") is not the adverse event scale. */
const LAB_ONLY = /laborator|\blabs?\b/i;
const AE_WORDS = /adverse event|\bAEs?\b|\bSAEs?\b/i;
/** The label without its grade prefix: "Grade 1 - Mild" and "Mild" are the same label. */
const labelCore = (l: string) => squash(l).replace(/^grade\s*\d+\s*[-:(]?\s*/, "").replace(/[()]/g, "").replace(/\s+(?:related|due|attributed|resulting)\b.*$/, "").trim();
function sameSeverityLabels(a: PfOption[], b: PfOption[]): boolean {
  return a.length === b.length && a.every((o, i) => labelCore(o.label) === labelCore(b[i].label));
}

function scaleCheck(ctx: Ctx, n: 1 | 2): PfResult {
  const kind = n === 1 ? "severity" : "causality";
  const what = n === 1 ? "severity" : "relationship";
  let list: { options: PfOption[]; source: string } | null = null;
  let analysis = ctx.analysis;
  if (n === 1) {
    // Only a scale about adverse events overall counts; one stated "for labs" does not.
    const all = factsOf(ctx.analysis, "aeSeverityScale");
    const usable = all.filter((f) => !(LAB_ONLY.test(f.text) && !AE_WORDS.test(f.text)));
    if (all.length > 0 && usable.length === 0) return skip(n, "the severity scale in the protocol reading is for laboratory results, not for adverse events");
    analysis = { ...ctx.analysis, aeSeverityScale: usable };
  }
  let p = protocolList(kind, analysis);
  // A scale the protocol only NAMES ("CTCAE ... Grade 1-5") keeps the check's own long-standing grade list;
  // the same grades, so a form carrying either wording passes (labelCore ignores the wording around the grade).
  if (n === 1 && p && / grades\b/.test(p.source) && ctcaeScale(analysis)) p = null;
  if (p) list = { options: p.options.map((o) => ({ code: o.code, label: o.label })), source: `${p.source} (automatic correction)` };
  else if (n === 1) list = ctcaeScale(analysis);
  if (!list) return skip(n, `the protocol reading states no adverse event ${what} scale`);
  const want = list;
  const targets: Array<{ form: string; f: PfField }> = [];
  for (const form of ctx.forms) {
    if (!isAeForm(form.name)) continue;
    for (const f of form.fields) if (concept(f) === kind) targets.push({ form: form.name, f });
  }
  if (targets.length === 0) return skip(n, `no adverse event form has a ${what} question to compare`);
  // Options that already carry the protocol's labels are never relabelled ("Mild" matches "Grade 1 - Mild").
  const bad = targets.filter((t) => (n === 1 ? !sameSeverityLabels(t.f.options, want.options) : !sameOptions(t.f.options, want.options)));
  if (bad.length === 0) return pass(n, `${targets.length} ${what} question(s) carry the protocol's scale`);
  return fail(n,
    `The protocol's ${what} scale is: ${want.options.map((o) => o.label).join(", ")}. ${bad.length} question(s) offer something different.`,
    bad.map((t) => `${t.form}: "${t.f.text}" offers ${t.f.options.map((o) => o.label).join(", ") || "no options"}, and the protocol says ${want.options.map((o) => o.label).join(", ")}`),
    true,
    bad.map((t) => ({ kind: "setOptions" as const, form: t.form, varName: t.f.varName, options: want.options, responseType: "single_select", source: want.source, why: `adverse event ${what} follows the protocol's scale` })));
}

// ── W3 / W4: demographics ──────────────────────────────────────────────────

const YN_PLATFORM: PfOption[] = [{ code: 1, label: "Yes" }, { code: 0, label: "No" }];

function yesNoList(analysis: any): { options: PfOption[]; fromProtocol: boolean } {
  const p = protocolList("yesno", analysis);
  return p ? { options: p.options.map((o) => ({ code: o.code, label: o.label })), fromProtocol: true } : { options: YN_PLATFORM, fromProtocol: false };
}

interface DemoTopic { key: string; protocol: RegExp; field: RegExp; label: string }
const DEMO_TOPICS: DemoTopic[] = [
  { key: "indigenous", protocol: /aborigin|torres strait|indigenous|first nations|m[aā]ori/i, field: /aborigin|torres strait|indigenous|first nations|m[aā]ori/i, label: "Aboriginal or Torres Strait Islander origin" },
  { key: "race", protocol: /\brace\b|racial/i, field: /\brace\b|racial/i, label: "race" },
  { key: "ethnicity", protocol: /ethnic|hispanic|latino/i, field: /ethnic|hispanic|latino/i, label: "ethnicity" },
  { key: "sex", protocol: /\bsex\b|gender/i, field: /\bsex\b|gender/i, label: "sex" },
  { key: "age", protocol: /\bage\b|date of birth|birth ?date|\bdob\b|year of birth/i, field: /\bage\b|birth|\bdob\b/i, label: "age or date of birth" },
  { key: "height", protocol: /height|stature/i, field: /height|stature/i, label: "height" },
  { key: "weight", protocol: /weight|\bbmi\b|body mass/i, field: /weight|\bbmi\b|body mass/i, label: "weight" },
];

function demographicsListed(analysis: any): { text: string; facts: Array<{ text: string; section?: string }> } {
  const facts = factsOf(analysis, "demographicsFields");
  return { text: facts.map((f) => f.text).join(" | ").toLowerCase(), facts };
}

function uniqueName(base: string, taken: Set<string>): string {
  let n = base.slice(0, 24);
  let i = 2;
  while (taken.has(n.toUpperCase())) n = `${base.slice(0, 22)}${i++}`;
  return n;
}

function demographicsCheck(ctx: Ctx): PfResult {
  const { text, facts } = demographicsListed(ctx.analysis);
  if (!text.trim()) return skip(3, "the protocol reading lists no demographic items");
  const demo = ctx.forms.filter((f) => isDemoForm(f.name));
  if (demo.length === 0) return skip(3, "this study has no demographics form yet");
  const formName = demo[0].name;
  // Height and weight are often on Vital Signs: a field on ANY form counts.
  const fields = ctx.forms.flatMap((f) => f.fields);
  const notCollected = (key: string) => (key === "race" || key === "ethnicity") && !!demographicNotCollected(ctx.analysis, key, ctx.text);
  const missing = DEMO_TOPICS.filter((t) => t.protocol.test(text) && !notCollected(t.key) && !fields.some((f) => t.field.test(ftext(f))));
  if (missing.length === 0) return pass(3, "every demographic item the protocol lists has a field");
  // WARN only. A repair here once invented an "Indigenous origin" field from a word inside the
  // protocol's own ethnicity list, so this check never adds a field; it names the item and the
  // protocol text and leaves the decision to the person.
  const items: string[] = [];
  for (const t of missing) {
    const fact = facts.find((f) => t.protocol.test(f.text));
    const where = fact ? `${sectionOf([fact])}: "${fact.text.slice(0, 160)}"` : "the protocol";
    items.push(`${formName}: no field for ${t.label}. The protocol names it in ${where}`);
  }
  return fail(3, `The protocol names these demographic items and no form has a field for them: ${missing.map((t) => t.label).join(", ")}. Nothing was added; please check whether the form needs them.`, items, false);
}

function studyInUs(setup: any): boolean | null {
  const list = countriesOf(setup);
  if (list.length === 0) return null;
  return list.some((c) => /^(us|usa|united states(?: of america)?)$/i.test(c.trim()));
}

function demographicsExtraCheck(ctx: Ctx): PfResult {
  const { text } = demographicsListed(ctx.analysis);
  if (!text.trim()) return skip(4, "the protocol reading lists no demographic items");
  const demo = ctx.forms.filter((f) => isDemoForm(f.name));
  if (demo.length === 0) return skip(4, "this study has no demographics form yet");
  const us = studyInUs(ctx.setup);
  if (us === null) return skip(4, "the study's countries are not known, so race and ethnicity cannot be judged");
  const saidNot = (["race", "ethnicity"] as const).some((k) => !!demographicNotCollected(ctx.analysis, k, ctx.text));
  if (us && !saidNot) return pass(4, "the study runs in the US, where race and ethnicity are reported");
  const extra: string[] = [];
  for (const form of demo) {
    for (const f of form.fields) {
      for (const t of DEMO_TOPICS.filter((x) => x.key === "race" || x.key === "ethnicity")) {
        const said = !!demographicNotCollected(ctx.analysis, t.key as "race" | "ethnicity", ctx.text);
        if (t.field.test(ftext(f)) && (!t.protocol.test(text) || said)) extra.push(`${form.name}: "${f.text}" asks for ${t.label}, which the protocol does not collect`);
      }
    }
  }
  if (extra.length === 0) return pass(4, "no race or ethnicity field beyond what the protocol collects");
  return fail(4, "This study does not run in the US and the protocol does not list these items. They are kept for you to decide.", extra, false);
}

// ── W5: stratification ─────────────────────────────────────────────────────

const STRAT_STOP = new Set(["baseline", "seated", "status", "level", "levels", "category", "the", "and", "for", "with", "mmhg", "prior", "stratum", "strata", "factor"]);

/** "baseline seated systolic BP < 160 / >= 160 mmHg" -> name and its two levels. */
export function parseStratum(raw: string): { name: string; levels: string[] } {
  const s = raw.replace(/\s+/g, " ").trim();
  const cmp = [...s.matchAll(/(<=|>=|≤|≥|<|>)\s*(\d+(?:\.\d+)?)/g)];
  const capFirst = (x: string) => (x ? x.charAt(0).toUpperCase() + x.slice(1) : x);
  if (cmp.length >= 2) {
    const name = s.slice(0, cmp[0].index).replace(/[\s(,:\-–]+$/, "").trim() || s;
    const unit = /\d\s*\)?\s*([A-Za-z%][A-Za-z%/.]*)\s*\)?\s*$/.exec(s)?.[1] ?? "";
    const levels = cmp.map((m) => `${m[1] === "≤" ? "<=" : m[1] === "≥" ? ">=" : m[1]} ${m[2]}${unit ? ` ${unit}` : ""}`);
    return { name: capFirst(name), levels };
  }
  const paren = /^(.*?)\s*\(([^)]*)\)\s*$/.exec(s);
  if (paren) {
    const parts = paren[2].split(/\s*(?:\/|;|,|\bor\b)\s*/i).map((p) => p.trim()).filter(Boolean);
    if (parts.length >= 2) return { name: capFirst(paren[1].trim()), levels: parts.map(capFirst) };
  }
  return { name: capFirst(s), levels: [] };
}

const stratTokens = (name: string) => low(name).split(/[^a-z0-9]+/).filter((t) => t.length >= 3 && !STRAT_STOP.has(t));

function countriesOf(setup: any): string[] {
  const raw = setup?.countries;
  return (Array.isArray(raw) ? raw : []).map((c: any) => str(typeof c === "string" ? c : c?.name ?? c?.code).trim()).filter(Boolean);
}

function stratificationCheck(ctx: Ctx): PfResult {
  const factors = [...new Set([...listOf(ctx.analysis?.stratificationFactors), ...listOf(ctx.analysis?.studyDesign?.stratificationFactors)])];
  if (factors.length === 0) return skip(5, "the protocol reading states no randomisation stratification factors");
  const rand = ctx.forms.filter((f) => isRandForm(f.name));
  if (rand.length === 0) return skip(5, "this study has no randomisation form yet");
  const formName = rand[0].name;
  const fields = rand.flatMap((f) => f.fields);
  const taken = new Set(fields.map((f) => f.varName.toUpperCase()));
  const items: string[] = [];
  const repair: PfOp[] = [];
  const source = "protocol stratification factors (automatic correction)";
  for (const raw of factors) {
    const { name, levels } = parseStratum(raw);
    let toks = stratTokens(name);
    if (toks.length === 0) toks = low(name).split(/[^a-z0-9]+/).filter((t) => t.length >= 2);
    const have = fields.find((f) => {
      const t = ftext(f);
      return toks.length > 0 && toks.filter((k) => hasWord(t, k)).length / toks.length >= 0.6;
    });
    const countries = /countr/i.test(name) && levels.length === 0 ? countriesOf(ctx.setup) : [];
    const lv = levels.length ? levels : countries.length >= 2 ? countries : [];
    const options: PfOption[] = lv.map((label, i) => ({ code: i + 1, label }));
    if (have) {
      if (options.length >= 2 && (have.options.length < options.length || !/select/.test(have.responseType))) {
        items.push(`${formName}: "${have.text}" is not a list of the protocol's levels (${lv.join(" / ")})`);
        repair.push({ kind: "setOptions", form: formName, varName: have.varName, options, responseType: "single_select", source, why: `stratification factor "${name}" lists the protocol's levels` });
      }
      continue;
    }
    items.push(`${formName}: no field for the stratification factor "${raw}"`);
    const stem = `STRAT${(toks[0] ?? "FACTOR").toUpperCase().replace(/[^A-Z0-9]/g, "")}${toks[1] ? toks[1].toUpperCase().replace(/[^A-Z0-9]/g, "") : ""}`;
    const varName = uniqueName(stem, taken);
    taken.add(varName.toUpperCase());
    repair.push({
      kind: "addField", form: formName, source, why: `the protocol stratifies randomisation by "${name}"`,
      field: { varName, text: name, responseType: options.length >= 2 ? "single_select" : "text", options, codesFrom: source },
    });
  }
  if (items.length === 0) return pass(5, `all ${factors.length} stratification factor(s) are recorded on the randomisation form`);
  return fail(5, `The protocol stratifies randomisation by ${factors.join("; ")}. The randomisation form does not record all of them.`, items, true, repair);
}

// ── W6: dose modification ──────────────────────────────────────────────────


/** True when the protocol reading or text describes a medical device rather than a drug. WARN-only either way. */
function isDeviceStudy(ctx: Ctx): boolean {
  const iv = low(corpusOf(ctx.analysis, ["interventions", "studyType", "investigationalProduct"]));
  const text = low(ctx.text).slice(0, 20000);
  const drugCue = /\bplacebo\b|\btablets?\b|\bcapsules?\b|\bmg\b|\binjection\b/.test(iv);
  return !drugCue && (/\bdevice\b/.test(iv) || /\biso 14155\b|\bdevice\b[^.]{0,20}\b(?:study|trial|investigation)\b/.test(text));
}

function doseCheck(ctx: Ctx): PfResult {
  const corpus = `${corpusOf(ctx.analysis, ["interventions", "safety", "treatment", "dosing", "dosageModifications", "doseModification", "doseModifications", "stoppingRules"])} | ${low(ctx.text)}`;
  const sentences = corpus.split(/[.;|\n]+/).map((x) => x.trim()).filter(Boolean);
  const denies = sentences.some(isStudyWideDoseDenial);
  const permits = sentences.some((x) => /dose/.test(x) && DOSE_PERMIT_WORDS.test(x) && !DOSE_DENIAL.test(x));
  if (isDeviceStudy(ctx)) return skip(6, "the protocol describes a device, which has no drug dose to compare against");
  if (!denies) return skip(6, "the protocol reading does not say, in a full sentence, that the study drug dose may not be changed");
  if (permits) return skip(6, "the protocol reading both forbids and permits dose changes, so no dose option can be called out of place");
  const bad: Array<{ form: string; f: PfField; labels: string[] }> = [];
  let seen = 0;
  for (const form of ctx.forms) {
    if (!isAeForm(form.name)) continue;
    for (const f of form.fields) {
      if (concept(f) !== "action") continue;
      seen++;
      const labels = f.options.filter((o) => /dose\s+(?:reduc|decreas|increas|escalat)/i.test(o.label)).map((o) => o.label);
      if (labels.length) bad.push({ form: form.name, f, labels });
    }
  }
  if (seen === 0) return skip(6, "no adverse event form has an action-taken question to compare");
  if (bad.length === 0) return pass(6, "no action-taken question offers a dose change");
  return fail(6,
    "The protocol states that the dose may not be changed (only interruption or discontinuation), yet an action-taken question offers a dose change. Nothing was changed; please check the options.",
    bad.map((b) => `${b.form}: "${b.f.text}" offers ${b.labels.join(" and ")}`),
    false);
}

// ── W7: oncology leftovers ─────────────────────────────────────────────────

const ONCOLOGY = /cancer|oncolog|tumou?r|carcinoma|lymphoma|leuk(?:a)?emi|melanoma|sarcoma|myeloma|malignan|metasta|recist|neoplas|chemotherap/i;
/** Wording that belongs to cancer trials only. "Disease progression" and "vital status" alone are not enough. */
const ONCOLOGY_WORDING = /progressive disease|\bRECIST\b|objective response|overall survival|survival follow[- ]?up/i;

function oncologyCheck(ctx: Ctx): PfResult {
  const corpus = corpusOf(ctx.analysis, ["indication", "condition", "conditions", "disease", "therapeuticArea", "studyTitle", "title", "protocolTitle", "objectives", "endpoints", "primaryEndpoint", "secondaryEndpoints", "studyDesign", "interventions"]);
  if (!corpus.trim()) return skip(7, "the protocol reading gives no indication or endpoints to tell whether this is a cancer study");
  if (ONCOLOGY.test(corpus)) return pass(7, "this is a cancer study, so progression and survival items belong");
  const hits: string[] = [];
  for (const form of ctx.forms) {
    for (const f of form.fields) {
      const opt = f.options.filter((o) => /progressive disease/i.test(o.label)).map((o) => o.label);
      if (opt.length) hits.push(`${form.name}: "${f.text}" offers ${opt.join(" and ")}`);
      if (ONCOLOGY_WORDING.test(`${f.text} ${f.varName}`)) hits.push(`${form.name}: "${f.text}" uses cancer-trial wording`);
    }
  }
  if (hits.length === 0) return pass(7, "no cancer-trial wording on the forms");
  // Shown, never blocking, never removed automatically: only a person can say whether the study follows survival.
  return fail(7, "This protocol does not look like a cancer study, yet a form carries cancer-trial wording. Nothing was removed.", hits, false);
}

// ── W8: named instruments ──────────────────────────────────────────────────

const INSTRUMENT_PREFIX_SKIP = new Set(["covid", "cov", "sars", "type", "phase", "grade", "visit", "part", "week", "day", "study", "cycle", "arm", "cohort", "period", "stage", "form", "page", "step", "section", "ich", "il", "tnf", "hba", "hla", "cd", "pre", "post", "non", "anti", "ctcae"]);
/** Instrument -> the words a protocol may use for it instead of the acronym. */
const INSTRUMENT_ALIASES: Array<{ key: string; names: string[] }> = [
  { key: "sf36", names: ["short form 36", "short form-36"] },
  { key: "sf12", names: ["short form 12", "short form-12"] },
  { key: "phq", names: ["patient health questionnaire"] },
  { key: "gad", names: ["generalized anxiety disorder", "generalised anxiety disorder"] },
  { key: "dlqi", names: ["dermatology life quality index"] },
  { key: "eq5d", names: ["euroqol"] },
  { key: "uas7", names: ["urticaria activity score"] },
  { key: "hads", names: ["hospital anxiety and depression"] },
  { key: "promis", names: ["patient-reported outcomes measurement information system", "patient reported outcomes measurement information system"] },
  { key: "tsqm", names: ["treatment satisfaction questionnaire for medication"] },
  { key: "mmse", names: ["mini-mental state", "mini mental state"] },
  { key: "moca", names: ["montreal cognitive assessment"] },
  { key: "bdi", names: ["beck depression inventory"] },
  { key: "kccq", names: ["kansas city cardiomyopathy questionnaire"] },
  { key: "womac", names: ["western ontario and mcmaster"] },
  { key: "madrs", names: ["montgomery-asberg", "montgomery asberg"] },
  { key: "edss", names: ["expanded disability status scale"] },
  { key: "sgrq", names: ["st george's respiratory questionnaire", "st georges respiratory questionnaire"] },
  { key: "ham", names: ["hamilton"] },
  { key: "mlhfq", names: ["minnesota living with heart failure"] },
];
const INSTRUMENT_NAMED = /\b(?:UAS-?7|TSQM|MMSE|MoCA|MADRS|PHQ|GAD|DLQI|KCCQ|EQ-?5D|SF-?\d+|WOMAC|HADS|BDI|CES-D|PROMIS|NPI|ADAS-Cog|UPDRS|EDSS|SGRQ|mMRC|AQLQ|HAM-?[AD]|MLHFQ|QLQ-?[A-Z0-9]+)\b/gi;
const INSTRUMENT_HYPHEN = /\b([A-Za-z]{2,8})-(\d{1,3}[A-Za-z]?)\b/g;
const norm = (s: string) => low(s).replace(/[^a-z0-9]/g, "");

function instrumentsIn(name: string): string[] {
  const found = new Set<string>();
  for (const m of name.matchAll(INSTRUMENT_NAMED)) found.add(m[0]);
  for (const m of name.matchAll(INSTRUMENT_HYPHEN)) if (!INSTRUMENT_PREFIX_SKIP.has(m[1].toLowerCase())) found.add(m[0]);
  // "TSQM-14" is found twice, once as "TSQM" and once whole. Keep the longest.
  const all = [...found];
  return all.filter((a) => !all.some((b) => b !== a && b.length > a.length && low(b).startsWith(low(a))));
}

function instrumentCheck(ctx: Ctx): PfResult {
  if (!ctx.analysis || Object.keys(ctx.analysis).length === 0) return skip(8, "there is no protocol reading to compare form names against");
  const raw = `${JSON.stringify(ctx.analysis)} ${ctx.text}`;
  const flat = norm(raw);
  const lowRaw = low(raw);
  const items: string[] = [];
  let named = 0;
  for (const form of ctx.forms) {
    for (const ins of instrumentsIn(form.name)) {
      named++;
      const prefix = low(ins).replace(/[^a-z]/g, "");
      const alias = INSTRUMENT_ALIASES.find((a) => norm(ins).startsWith(a.key));
      const inProtocol = flat.includes(norm(ins)) || (prefix.length >= 3 && new RegExp(`\\b${prefix}\\b`).test(lowRaw))
        || (!!alias && alias.names.some((n) => lowRaw.includes(n) || flat.includes(norm(n))));
      if (!inProtocol) items.push(`${form.name}: built on ${ins}, which the protocol never names`);
    }
  }
  if (named === 0) return pass(8, "no form is built on a named questionnaire or scale");
  if (items.length === 0) return pass(8, `${named} named instrument(s) all appear in the protocol`);
  return fail(8, "A form is built on a published instrument the protocol reading never names. Confirm the protocol requires it, or remove the form.", items, false);
}

// ── W9 / W10: the serious adverse event form ───────────────────────────────

interface CritTopic { key: string; crit: RegExp; field: RegExp; code: string; text: string }
const CRIT_TOPICS: CritTopic[] = [
  { key: "death", crit: /death|fatal|\bdied\b/i, field: /death|died|fatal/i, code: "SAEDEATH", text: "Results in death" },
  { key: "life", crit: /life[- ]threat/i, field: /life[- ]threat/i, code: "SAELIFE", text: "Is life-threatening" },
  { key: "hosp", crit: /hospitali[sz]|inpatient/i, field: /hospital|inpatient/i, code: "SAEHOSP", text: "Requires inpatient hospitalisation or prolongation of existing hospitalisation" },
  { key: "disab", crit: /disab|incapacit/i, field: /disab|incapacit/i, code: "SAEDISAB", text: "Results in persistent or significant disability or incapacity" },
  { key: "congen", crit: /congenital|birth defect|anomal/i, field: /congenital|birth defect|anomal/i, code: "SAECONG", text: "Is a congenital anomaly or birth defect" },
  { key: "medical", crit: /important medical|medically significant|medical event/i, field: /important medical|medically significant|medical event/i, code: "SAEMED", text: "Is an important medical event" },
];
const NOT_A_FLAG = /\bdate\b|cause|autops|time\b|describe|narrative/i;

function ynField(ctx: Ctx, varName: string, text: string, source: string): PfField {
  const yn = yesNoList(ctx.analysis);
  return { varName, text, responseType: yn.fromProtocol ? "single_select" : "yesno", options: yn.options, codesFrom: source };
}

function saeCriteriaCheck(ctx: Ctx): PfResult {
  const facts = factsOf(ctx.analysis, "seriousnessCriteria");
  if (facts.length === 0) return skip(9, "the protocol reading states no seriousness criteria");
  const sae = ctx.forms.filter((f) => isSaeForm(f.name));
  if (sae.length === 0) return skip(9, "this study has no serious adverse event form yet");
  const formName = sae[0].name;
  const fields = sae.flatMap((f) => f.fields);
  const taken = new Set(fields.map((f) => f.varName.toUpperCase()));
  const items: string[] = [];
  const repair: PfOp[] = [];
  const add = (code: string, text: string, fact: { text: string; section?: string }) => {
    items.push(`${formName}: no field for the criterion "${text}"`);
    const source = sourceFor([fact]);
    const varName = uniqueName(code, taken);
    taken.add(varName.toUpperCase());
    repair.push({ kind: "addField", form: formName, source, why: "every seriousness criterion the protocol gives has a field", field: ynField(ctx, varName, text, source) });
  };
  for (const t of CRIT_TOPICS) {
    const fact = facts.find((f) => t.crit.test(f.text));
    if (!fact) continue;
    if (fields.some((f) => t.field.test(ftext(f)) && !NOT_A_FLAG.test(f.text))) continue;
    add(t.code, t.text, fact);
  }
  // A criterion that matches no topic above is judged by its own words.
  facts.forEach((fact, i) => {
    if (CRIT_TOPICS.some((t) => t.crit.test(fact.text))) return;
    const toks = low(fact.text).split(/[^a-z0-9]+/).filter((w) => w.length >= 5 && !/^(results|requires|event|serious|criterion|criteria|medical|considered)$/.test(w));
    if (toks.length === 0) return;
    if (fields.some((f) => toks.filter((w) => ftext(f).includes(w)).length / toks.length >= 0.6)) return;
    add(`SAECRIT${i + 1}`, fact.text.replace(/\.$/, ""), fact);
  });
  if (items.length === 0) return pass(9, `every one of the ${facts.length} seriousness criteria has a field`);
  return fail(9, `The protocol gives ${facts.length} seriousness criteria and the serious adverse event form misses some.`, items, false, repair);
}

const BODIES = /\b(TGA|HREC|FDA|EMA|MHRA|IRB|IEC|PMDA|Health Canada|ethics committee|DSMB|DMC)\b/gi;
const GENERIC_REL = new Set(["relationship", "relation", "related", "relatedness", "causality", "to", "of", "the", "event", "adverse", "serious", "study", "drug", "treatment", "product", "investigational", "ip", "medication", "sae", "ae", "is", "was", "assessment", "investigator", "as", "by", "for", "and", "a", "an", "with", "intervention", "causal"]);

/**
 * The concept a duplicate would share, or null when the field is not one the rule judges.
 * Only Yes/No criterion flags and relationship questions are judged: a text, date or
 * attachment field ("Hospital name", "Place of death", "Death certificate attached") never is.
 */
export function saeConceptKey(f: PfField): string | null {
  const t = f.text.toLowerCase();
  if (concept(f) === "causality" || (/relationship|relatedness|causality|related to/.test(t) && /study|drug|treatment|product|investigational|\bip\b/.test(t))) {
    const rest = t.split(/[^a-z0-9]+/).filter((w) => w && !GENERIC_REL.has(w));
    return rest.length === 0 ? "relationship" : null;
  }
  if (!isYesNoField(f)) return null;
  const bodies = [...new Set([...f.text.matchAll(BODIES)].map((m) => m[1].toLowerCase()))].sort();
  if (bodies.length && /report|notif|inform|sent|submit/i.test(t) && !/\bdate\b/.test(t)) return `report:${bodies.join("+")}:flag`;
  for (const topic of CRIT_TOPICS) {
    if (topic.field.test(ftext(f)) && !NOT_A_FLAG.test(f.text) && !/attach|certificate|name|place|where|specify|describe/i.test(f.text)) return `criterion:${topic.key}`;
  }
  return null;
}

function saeDuplicatesCheck(ctx: Ctx): PfResult {
  const sae = ctx.forms.filter((f) => isSaeForm(f.name));
  if (sae.length === 0) return skip(10, "this study has no serious adverse event form yet");
  const items: string[] = [];
  const repair: PfOp[] = [];
  for (const form of sae) {
    const seen = new Map<string, PfField>();
    for (const f of form.fields) {
      const k = saeConceptKey(f);
      if (!k) continue;
      const first = seen.get(k);
      if (!first) { seen.set(k, f); continue; }
      const label = k === "relationship" ? "relationship to the study drug" : k.startsWith("report:") ? `reporting to ${k.split(":")[1].toUpperCase().replace("+", "/")}` : "the same seriousness criterion";
      // Two relationship questions are the same only when both are lists with the same choices.
      const sameList = k !== "relationship" || (/select/.test(f.responseType) && /select/.test(first.responseType) && sameOptionSet(f.options, first.options));
      if (!sameList) { items.push(`${form.name}: "${f.text}" may repeat "${first.text}" (${label}), but the choices differ, so it was kept`); continue; }
      items.push(`${form.name}: "${f.text}" repeats "${first.text}" (${label})`);
      if (!fromSponsor(f)) repair.push({ kind: "removeField", form: form.name, varName: f.varName, source: "serious adverse event form, duplicate removed (automatic correction)", why: `one field is enough for ${label}` });
    }
  }
  if (items.length === 0) return pass(10, "no concept is asked twice on the serious adverse event form");
  return fail(10, "The serious adverse event form asks the same thing twice. Only a Yes/No flag or a relationship list with identical choices is removed, and the later copy is the one removed.", items, false, repair);
}

// ── W11 / W12: coding conventions ──────────────────────────────────────────

const isYes = (l: string) => /^yes\b|^y$/i.test(l.trim());
const isNo = (l: string) => /^no\b|^n$/i.test(l.trim());
const UNKNOWNISH = /unknown|not (?:applicable|done|asked|available|assessed)|n\/?a|refused|not stated/i;
const isYesNoType = (f: PfField) => f.responseType === "yesno" || f.responseType === "yes_no";

function isYesNoField(f: PfField): boolean {
  if (isYesNoType(f)) return true;
  if (!/select|radio|coded/.test(f.responseType)) return false;
  const o = f.options;
  if (o.length < 2 || o.length > 3) return false;
  return o.some((x) => isYes(x.label)) && o.some((x) => isNo(x.label)) && o.filter((x) => !isYes(x.label) && !isNo(x.label)).every((x) => UNKNOWNISH.test(x.label));
}

interface YnConv { options: PfOption[]; source: string; scope: string[]; general: boolean; sig: string }
const YN_SCOPE_STOP = new Set(["coded", "code", "codes", "fields", "field", "yes", "other", "others", "answers", "values", "convention", "appendix", "with", "using", "are", "all", "rest", "remaining", "otherwise", "questions", "question", "the", "and", "for", "use", "used"]);

/** Every Yes/No coding the protocol states, each with the words that say which fields it governs. */
function yesNoConventions(analysis: any): YnConv[] {
  const out: YnConv[] = [];
  for (const f of factsOf(analysis, "codingConventions")) {
    const clauses = f.codes && f.codes.length >= 2 ? [f.text] : f.text.split(/;|\.\s|,\s+(?=[a-z])/i).map((c) => c.trim()).filter(Boolean);
    for (const clause of clauses) {
      let options: PfOption[] | null = null;
      const pairs = f.codes && f.codes.length >= 2 ? f.codes : parseCodePairs(clause);
      if (pairs && pairs.some((o) => isYes(o.label)) && pairs.some((o) => isNo(o.label))) {
        const y = pairs.find((o) => isYes(o.label))!;
        const n = pairs.find((o) => isNo(o.label))!;
        options = [{ code: y.code, label: "Yes" }, { code: n.code, label: "No" }];
      } else if (/\bY\s*\/\s*N\b/.test(clause)) options = [{ code: "Y", label: "Yes" }, { code: "N", label: "No" }];
      else if (/\b1\s*\/\s*0\b/.test(clause) && /yes|no/i.test(clause)) options = [{ code: 1, label: "Yes" }, { code: 0, label: "No" }];
      if (!options) continue;
      const general = /all other|otherwise|remaining|all fields|all yes|every yes/i.test(clause);
      const scope = general ? [] : `${low(clause)} ${low(f.field)}`.split(/[^a-z0-9]+/).filter((w) => w.length >= 4 && !YN_SCOPE_STOP.has(w) && !/^\d+$/.test(w));
      out.push({ options, source: `${sectionOf([f])} (automatic correction)`, scope, general: general || scope.length === 0, sig: options.map((o) => `${o.code}`).join("/") });
    }
  }
  return out;
}

function ynCodes(f: PfField): { y?: string; n?: string } {
  const y = f.options.find((o) => isYes(o.label));
  const n = f.options.find((o) => isNo(o.label));
  if (isYesNoType(f) && !y && !n) return { y: "1", n: "0" };
  return { y: y ? String(y.code) : undefined, n: n ? String(n.code) : undefined };
}
const matchesConv = (f: PfField, c: YnConv) => {
  const k = ynCodes(f);
  return k.y === String(c.options[0].code) && k.n === String(c.options[1].code);
};

function yesNoCheck(ctx: Ctx): PfResult {
  let convs = yesNoConventions(ctx.analysis);
  if (convs.length === 0) {
    const p = protocolList("yesno", ctx.analysis);
    if (!p) return skip(11, "the protocol reading states no coding for Yes and No");
    convs = [{ options: p.options.map((o) => ({ code: o.code, label: o.label })), source: `${p.source} (automatic correction)`, scope: [], general: true, sig: "" }];
  }
  const fields: Array<{ form: string; f: PfField }> = [];
  for (const form of ctx.forms) for (const f of form.fields) if (isYesNoField(f) && !fromSponsor(f)) fields.push({ form: form.name, f });
  if (fields.length === 0) return skip(11, "no form has a Yes or No question to compare");
  const mixed = new Set(convs.map((c) => c.sig)).size > 1;
  const bad: Array<{ form: string; f: PfField; conv: YnConv }> = [];
  const unsure: Array<{ form: string; f: PfField }> = [];
  for (const { form, f } of fields) {
    if (!mixed) {
      if (!matchesConv(f, convs[0])) bad.push({ form, f, conv: convs[0] });
      continue;
    }
    // Several codings are stated: a field that already follows ANY of them is never rewritten,
    // and a convention applies only to a field it clearly names.
    if (convs.some((c) => matchesConv(f, c))) continue;
    const where = ` ${low(form)} ${ftext(f)} `;
    const named = convs.find((c) => !c.general && c.scope.some((w) => hasWord(where, w)));
    if (named) bad.push({ form, f, conv: named });
    else unsure.push({ form, f });
  }
  if (bad.length === 0 && unsure.length === 0) return pass(11, `${fields.length} Yes/No question(s) use the protocol's codes`);
  const stated = convs.map((c) => `${c.options[0].code}/${c.options[1].code}`).filter((v, i, a) => a.indexOf(v) === i).join(" or ");
  const items = [
    ...bad.map(({ form, f, conv }) => `${form}: "${f.text}" is coded ${optString(f.options) || "1=Yes; 0=No"}, and the protocol codes it ${conv.options[0].code}/${conv.options[1].code}`),
    ...unsure.map(({ form, f }) => `${form}: "${f.text}" is coded ${optString(f.options) || "1=Yes; 0=No"}, and the protocol states more than one coding (${stated}) without saying which applies here`),
  ];
  return fail(11,
    mixed ? `The protocol states more than one Yes/No coding (${stated}). ${items.length} question(s) follow none that clearly applies.` : `The protocol codes Yes as ${convs[0].options[0].code} and No as ${convs[0].options[1].code}. ${bad.length} question(s) use other codes.`,
    items,
    bad.length > 0,
    bad.map(({ form, f, conv }) => {
      const yes = conv.options[0];
      const no = conv.options[1];
      const standard = String(yes.code) === "1" && String(no.code) === "0";
      const extras = f.options.filter((o) => !isYes(o.label) && !isNo(o.label));
      const options = [{ code: yes.code, label: "Yes" }, { code: no.code, label: "No" }, ...extras];
      return { kind: "setOptions" as const, form, varName: f.varName, options, responseType: standard && extras.length === 0 && isYesNoType(f) ? f.responseType : "single_select", source: conv.source, why: "Yes and No follow the protocol's coding" };
    }));
}

function sexCheck(ctx: Ctx): PfResult {
  const conv = protocolList("sex", ctx.analysis);
  if (!conv) return skip(12, "the protocol reading states no coding for sex");
  const source = `${conv.source} (automatic correction)`;
  // A study that enrols one sex codes that sex only; the other sex's code is not offered.
  const options = restrictSexOptions(conv.options.map((o) => ({ code: o.code, label: o.label })), singleSexRestriction(ctx.analysis));
  const fields: Array<{ form: string; f: PfField }> = [];
  for (const form of ctx.forms) for (const f of form.fields) if (concept(f) === "sex" && !fromSponsor(f)) fields.push({ form: form.name, f });
  if (fields.length === 0) return skip(12, "no form has a sex question to compare");
  const bad = fields.filter(({ f }) => !sameOptionSet(f.options, options));
  if (bad.length === 0) return pass(12, `${fields.length} sex question(s) use the protocol's codes`);
  return fail(12, `The protocol codes sex as ${optString(options)}. ${bad.length} question(s) use other codes.`,
    bad.map(({ form, f }) => `${form}: "${f.text}" is coded ${optString(f.options)}`), true,
    bad.map(({ form, f }) => ({ kind: "setOptions" as const, form, varName: f.varName, options, responseType: "single_select", source, why: "sex follows the protocol's coding" })));
}

// ── W13: follow-up contacts ────────────────────────────────────────────────

const CONTACT_VISIT = /telephone|\bphone\b|\bcall\b|remote (?:contact|visit)|contact (?:visit|only)|by phone/i;
const norm1 = (s: unknown) => low(s).replace(/[^a-z0-9]+/g, " ").trim();

function contactVisits(ctx: Ctx): string[] {
  const names: string[] = [];
  const add = (v: any) => {
    const n = str(v?.name ?? v?.id).trim();
    if (n && CONTACT_VISIT.test(`${n} ${str(v?.type)} ${str(v?.mode)}`) && !names.some((x) => norm1(x) === norm1(n))) names.push(n);
  };
  for (const v of Array.isArray(ctx.design?.visits) ? ctx.design.visits : []) add(v);
  for (const v of Array.isArray(ctx.analysis?.schedule?.visits) ? ctx.analysis.schedule.visits : []) add(v);
  return names;
}

function followUpCheck(ctx: Ctx): PfResult {
  const visits = contactVisits(ctx);
  if (visits.length === 0) return skip(13, "the schedule lists no telephone or remote follow-up contact");
  // Adverse events and concomitant medications are LOG forms: any form that can take them serves every contact.
  const capable = (re: RegExp) => ctx.forms.some((f) => re.test(f.name) || f.fields.some((x) => re.test(x.text)));
  const assessments = collectAssessments(ctx.design, ctx.analysis);
  const items: string[] = [];
  const aeOk = capable(/adverse event|\bAEs?\b/i);
  const cmOk = capable(/concomitant|\bcon[- ]?meds?\b/i);
  const pregOk = capable(/pregnan/i);
  for (const v of visits) {
    const missing: string[] = [];
    if (!aeOk) missing.push("adverse events");
    if (!cmOk) missing.push("concomitant medications");
    // Pregnancy status is wanted only when the schedule puts a pregnancy test at THIS contact.
    const pregnancyHere = assessments.some((a) => norm1(a.visit) === norm1(v) && /pregnan/i.test(a.label));
    if (pregnancyHere && !pregOk) missing.push("pregnancy status");
    if (missing.length) items.push(`${v}: no form in the study collects ${missing.join(", ")}`);
  }
  if (items.length === 0) return pass(13, `${visits.length} follow-up contact(s) can record adverse events and medications on the study's forms`);
  return fail(13, "A scheduled follow-up contact has no form that can collect the safety information the protocol needs at it.", items, false);
}

// ── W14: PK sampling times ─────────────────────────────────────────────────

const PK_ASSESSMENT = /pharmacokinetic|\bPK\b|plasma concentration|trough/i;
const TIMING = {
  predose: /pre[- ]?dose|trough/i,
  postdose: /post[- ]?dose|after (?:the )?dose/i,
} as const;

function pkCheck(ctx: Ctx): PfResult {
  const pk = ctx.forms.filter((f) => isPkForm(f.name));
  if (pk.length === 0) return skip(14, "this study has no pharmacokinetic form yet");
  const scheduled = collectAssessments(ctx.design, ctx.analysis).map((a) => a.label).filter((l) => PK_ASSESSMENT.test(l));
  if (scheduled.length === 0) return skip(14, "the schedule lists no pharmacokinetic sample to compare against");
  const text = scheduled.join(" | ");
  const allowed = { predose: TIMING.predose.test(text), postdose: TIMING.postdose.test(text) };
  if (!allowed.predose && !allowed.postdose) return skip(14, "the schedule gives no pre-dose or post-dose sampling times for the pharmacokinetic samples");
  const items: string[] = [];
  for (const form of pk) {
    const bits: Array<{ where: string; s: string }> = [];
    for (const f of form.fields) {
      bits.push({ where: `"${f.text}"`, s: f.text });
      for (const o of f.options) bits.push({ where: `"${f.text}" option "${o.label}"`, s: o.label });
    }
    // Only a named timepoint the protocol does not schedule: "Pre-dose (within 60 min)" and "Actual collection time" never flag.
    for (const b of bits) {
      const kinds = (["predose", "postdose"] as const).filter((k) => !allowed[k] && TIMING[k].test(b.s)).map((k) => (k === "postdose" ? "post-dose" : "pre-dose"));
      if (kinds.length) items.push(`${form.name}: ${b.where} names a ${kinds.join(" and ")} sample the protocol does not schedule`);
    }
  }
  const uniq = [...new Set(items)];
  if (uniq.length === 0) return pass(14, "the pharmacokinetic form names only sampling times the protocol schedules");
  return fail(14, `The schedule lists only these pharmacokinetic samples: ${scheduled.join("; ")}. The form names times that are not scheduled.`, uniq, false);
}

// ── Running and repairing ──────────────────────────────────────────────────

export function runProtocolFactChecks(input: PfInput): PfResult[] {
  const ctx: Ctx = {
    analysis: input?.analysis && typeof input.analysis === "object" ? input.analysis : {},
    setup: input?.studySetup ?? null,
    design: input?.studyDesign ?? null,
    mapping: input?.visitMapping ?? null,
    forms: toPfForms(input?.forms ?? []),
    text: str(input?.protocolText),
  };
  const run = (n: number, fn: (c: Ctx) => PfResult): PfResult => (ctx.forms.length === 0 ? skip(n, "this study has no forms yet") : fn(ctx));
  return [
    run(1, (c) => scaleCheck(c, 1)),
    run(2, (c) => scaleCheck(c, 2)),
    run(3, demographicsCheck),
    run(4, demographicsExtraCheck),
    run(5, stratificationCheck),
    run(6, doseCheck),
    run(7, oncologyCheck),
    run(8, instrumentCheck),
    run(9, saeCriteriaCheck),
    run(10, saeDuplicatesCheck),
    run(11, yesNoCheck),
    run(12, sexCheck),
    run(13, followUpCheck),
    run(14, pkCheck),
  ];
}

/** Apply operations to normalised forms. Returns new forms; the input is not touched. */
export function applyOps(forms: PfForm[], ops: PfOp[]): { forms: PfForm[]; applied: PfApplied[] } {
  const out: PfForm[] = forms.map((f) => ({ ...f, fields: f.fields.map((x) => ({ ...x, options: x.options.map((o) => ({ ...o })) })) }));
  const applied: PfApplied[] = [];
  for (const op of ops) {
    const form = out.find((f) => f.name === op.form);
    if (!form) continue;
    const rec = (): void => { applied.push({ form: op.form, varName: op.kind === "addField" ? op.field.varName : op.varName, kind: op.kind, why: op.why, source: op.source }); };
    if (op.kind === "addField") {
      if (form.fields.some((f) => f.varName.toUpperCase() === op.field.varName.toUpperCase())) continue;
      form.fields.push({ ...op.field, options: op.field.options.map((o) => ({ ...o })), codesFrom: op.source });
      rec();
      continue;
    }
    const idx = form.fields.findIndex((f) => f.varName === op.varName);
    if (idx < 0) continue;
    const f = form.fields[idx];
    // The sponsor's adopted dictionary outranks the protocol (crf/crfAuthority.ts): its lists are never rewritten here.
    if ((op.kind === "setOptions" || op.kind === "removeOptions") && /^sponsor dictionary/i.test(f.codesFrom ?? "")) continue;
    if (op.kind === "setOptions") {
      f.options = op.options.map((o) => ({ ...o }));
      if (op.responseType) f.responseType = op.responseType;
      f.codesFrom = op.source;
      rec();
    } else if (op.kind === "removeOptions") {
      const drop = new Set(op.labels.map(squash));
      const before = f.options.length;
      f.options = f.options.filter((o) => !drop.has(squash(o.label)));
      if (f.options.length !== before) { f.codesFrom = op.source; rec(); }
    } else {
      form.fields.splice(idx, 1);
      rec();
    }
  }
  return { forms: out, applied };
}

/**
 * Run the checks, apply every repair, and run them again (at most three passes,
 * because a repair can expose the next: a field added can be a duplicate).
 */
export function repairProtocolFacts(input: PfInput): { forms: PfForm[]; applied: PfApplied[]; results: PfResult[] } {
  let forms = toPfForms(input?.forms ?? []);
  const applied: PfApplied[] = [];
  let results = runProtocolFactChecks({ ...input, forms });
  for (let round = 0; round < 3; round++) {
    const ops = results.flatMap((r) => (r.status === "FAIL" ? r.repair ?? [] : []));
    if (ops.length === 0) break;
    const step = applyOps(forms, ops);
    if (step.applied.length === 0) break;
    forms = step.forms;
    applied.push(...step.applied);
    results = runProtocolFactChecks({ ...input, forms });
  }
  return { forms, applied, results };
}

// ── The generation pipeline ────────────────────────────────────────────────

export interface PfVariableContext {
  formName?: string;
  analysis: any;
  studySetup?: any;
  studyDesign?: any;
  /** The full protocol text, when the caller has it. */
  protocolText?: string;
}

export interface PfVariableChange { varName: string; concept: null; from: string; to: string; layer: "protocol"; source: string }
export interface PfVariableProvenance { varName: string; concept: null; layer: "protocol"; source: string; changed: boolean }

/**
 * Repair ONE generated form's variables, right after variable generation. The
 * form-level repairs (scales, coding, added and removed fields) apply; checks
 * that need the rest of the form set can only flag and are left to the gate and
 * the verification family.
 */
export function repairVariablesForProtocolFacts<V extends AuthVariable>(
  variables: V[],
  ctx: PfVariableContext,
): { variables: V[]; changes: PfVariableChange[]; provenance: PfVariableProvenance[] } {
  const none = { variables, changes: [] as PfVariableChange[], provenance: [] as PfVariableProvenance[] };
  const name = str(ctx?.formName).trim();
  if (!name || !ctx?.analysis) return none;
  const r = repairProtocolFacts({
    analysis: ctx.analysis, studySetup: ctx.studySetup, studyDesign: ctx.studyDesign, protocolText: ctx.protocolText,
    forms: [{ name, variables: variables.map((v) => ({ varName: v.varName, questionText: v.questionText, responseType: v.responseType, options: v.options, codesFrom: v.codesFrom })) }],
  });
  if (r.applied.length === 0) return none;
  const fixed = r.forms[0].fields;
  const byName = new Map(variables.map((v) => [str(v.varName), v]));
  const out: V[] = fixed.map((f) => {
    const orig = byName.get(f.varName);
    if (!orig) return { varName: f.varName, questionText: f.text, responseType: f.responseType, options: f.options.map((o) => ({ ...o })), unit: "", ...(f.codesFrom ? { codesFrom: f.codesFrom } : {}) } as unknown as V;
    const touched = f.codesFrom !== orig.codesFrom || orig.responseType !== f.responseType || !sameOptions(optionsOf(orig.options), f.options);
    return touched ? ({ ...orig, responseType: f.responseType, options: f.options.map((o) => ({ ...o })), ...(f.codesFrom ? { codesFrom: f.codesFrom } : {}) } as V) : orig;
  });
  const changes: PfVariableChange[] = [];
  const provenance: PfVariableProvenance[] = [];
  for (const a of r.applied) {
    const orig = byName.get(a.varName);
    const now = fixed.find((f) => f.varName === a.varName);
    changes.push({ varName: a.varName, concept: null, from: orig ? optString(optionsOf(orig.options)) || a.kind : "(not on the form)", to: now ? optString(now.options) || a.kind : "(removed)", layer: "protocol", source: `${a.source}: ${a.why} [${a.kind}]` });
    if (now) provenance.push({ varName: a.varName, concept: null, layer: "protocol", source: a.source, changed: true });
  }
  return { variables: out, changes, provenance };
}

// ── The approval gate ──────────────────────────────────────────────────────

export interface PfGateResult { ok: boolean; problems: PfResult[]; message: string }

/**
 * The CRF Builder's approval gate. Approve and Approve All refuse while any
 * blocking protocol-fact check fails, naming each problem in plain words.
 */
export const PF_CHECKING_MESSAGE = "Checking the forms against the protocol...";

export function protocolFactGate(input: PfInput & { loaded?: boolean }): PfGateResult {
  // Not loaded yet means not known: approval waits rather than reporting ok on nothing.
  if (input?.loaded === false) return { ok: false, problems: [], message: PF_CHECKING_MESSAGE };
  const problems = runProtocolFactChecks(input).filter((r) => r.status === "FAIL" && r.blocking);
  if (problems.length === 0) return { ok: true, problems: [], message: "" };
  // Per form: which checks hold it back, and what is wrong.
  const perForm = new Map<string, string[]>();
  for (const p of problems) {
    for (const item of p.items.length ? p.items : [`${PF_NO_FORM}: ${p.detail}`]) {
      const cut = item.indexOf(": ");
      const form = cut > 0 ? item.slice(0, cut) : PF_NO_FORM;
      const rest = cut > 0 ? item.slice(cut + 2) : item;
      (perForm.get(form) ?? perForm.set(form, []).get(form)!).push(`${p.title} (${rest})`);
    }
  }
  const lines = [...perForm.entries()].map(([form, list]) => `${form}: ${list.slice(0, 3).join("; ")}${list.length > 3 ? ` and ${list.length - 3} more` : ""}.`);
  return {
    ok: false,
    problems,
    message: `Cannot approve yet. These forms do not match the protocol. ${lines.join(" ")} Correct them in the forms, then approve.`,
  };
}
const PF_NO_FORM = "The study";
